import React, { useState } from 'react';
import logger from '../../utils/logger';
import { PaymentSettings, EmailSettings } from '../../types';
import PaymentSettingsForm from '../../components/admin/PaymentSettingsForm';
import EmailSettingsForm from '../../components/admin/EmailSettingsForm';
import { UserCircleIcon, CreditCardIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { PageTitle } from '../../components/ui';

const AccountPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'payment' | 'email'>('payment');
  const [isLoading, setIsLoading] = useState(false);

  // モックデータ（実際の実装では適切なデータソースから取得）
  const [paymentSettings] = useState<PaymentSettings>({
    id: 'payment-1',
    companyId: 'demo-company',
    subscriptionStatus: 'active',
    subscriptionPlan: 'プロフェッショナルプラン',
    billingEmail: 'billing@example.com',
    paymentMethods: [
      {
        id: 'pm_1',
        type: 'card',
        last4: '4242',
        brand: 'visa',
        expiryMonth: 12,
        expiryYear: 2025,
        isDefault: true,
        createdAt: '2024-01-01T00:00:00Z',
      },
    ],
    billingHistory: [
      {
        id: 'inv_1',
        amount: 9800,
        currency: 'JPY',
        status: 'paid',
        description: 'プロフェッショナルプラン - 2024年1月',
        invoiceUrl: '#',
        paidAt: '2024-01-01T00:00:00Z',
        createdAt: '2024-01-01T00:00:00Z',
      },
    ],
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  });

  const [emailSettings] = useState<EmailSettings>({
    id: 'email-1',
    companyId: 'demo-company',
    primaryEmail: 'admin@example.com',
    notificationEmails: ['manager@example.com'],
    emailNotifications: {
      scheduleChanges: true,
      systemAlerts: true,
      billingUpdates: true,
      securityNotifications: true,
      weeklyReports: false,
    },
    emailVerificationStatus: 'verified',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  });

  // アカウント管理のハンドラー（モック実装）
  const handleUpdatePaymentSettings = async (updates: Partial<PaymentSettings>) => {
    setIsLoading(true);
    try {
      logger.info('決済設定を更新:', updates);
      // TODO: 実際のAPI呼び出しを実装
      await new Promise(resolve => setTimeout(resolve, 1000));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmailSettings = async (updates: Partial<EmailSettings>) => {
    setIsLoading(true);
    try {
      logger.info('メール設定を更新:', updates);
      // TODO: 実際のAPI呼び出しを実装
      await new Promise(resolve => setTimeout(resolve, 1000));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='max-w-6xl mx-auto p-6'>
      {/* ヘッダー */}
      <div className='mb-8'>
        <PageTitle icon={UserCircleIcon} title='アカウント管理' iconSize='lg' />
        <p className='text-gray-600 dark:text-gray-300'>決済情報とメール設定を管理します。</p>
      </div>

      {/* タブナビゲーション */}
      <div className='mb-8'>
        <nav className='flex space-x-8 border-b border-gray-200 dark:border-gray-700 overflow-x-auto'>
          {[
            { id: 'payment', label: '決済情報', icon: CreditCardIcon },
            { id: 'email', label: 'メール設定', icon: EnvelopeIcon },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id as 'payment' | 'email')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className='flex items-center space-x-2'>
                <Icon className='h-4 w-4' />
                <span>{label}</span>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* タブコンテンツ */}
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6'>
        {activeTab === 'payment' && (
          <PaymentSettingsForm
            settings={paymentSettings}
            onUpdate={handleUpdatePaymentSettings}
            isLoading={isLoading}
          />
        )}
        {activeTab === 'email' && (
          <EmailSettingsForm
            settings={emailSettings}
            onUpdate={handleUpdateEmailSettings}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default AccountPage;
